import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, GraduationCap } from 'lucide-react';

const ARCHIVE_REASONS = [
  { value: 'error', label: 'Entered in Error' },
  { value: 'medical', label: 'Medical Disqualification' },
  { value: 'misconduct', label: 'Revoked - Misconduct' },
  { value: 'expired', label: 'Expired (not renewed)' },
  { value: 'superseded', label: 'Superseded by New Certificate' },
  { value: 'other', label: 'Other (specify in notes)' }
];

const ArchiveCertificateModal = ({ isOpen, onClose, onConfirm, certificate }) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!reason) {
      setError('Please select a reason for archiving');
      return;
    }

    if (reason === 'other' && !notes.trim()) {
      setError('Please provide details in the notes field');
      return;
    }

    onConfirm({
      reason: ARCHIVE_REASONS.find(r => r.value === reason)?.label || reason,
      notes: notes.trim()
    });

    // Reset form
    setReason('');
    setNotes('');
    setError('');
  };

  const handleClose = () => {
    setReason('');
    setNotes('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Archive Certificate
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Certificate Info */}
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <p className="text-sm font-medium text-gray-900">{certificate?.type}</p>
              <p className="text-sm text-gray-600">Number: {certificate?.number || 'N/A'}</p>
              <p className="text-sm text-gray-600">
                Expires: {formatDate(certificate?.expiry_date)}
              </p>
              {certificate?.source === 'training' && (
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <GraduationCap className="h-3.5 w-3.5" />
                  Issued via Training - Original training record will be preserved
                </p>
              )}
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                Archiving will hide this certificate from the active list. This action creates an audit trail and can be reversed.
              </p>
            </div>

            {/* Reason Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Archiving <span className="text-red-500">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
              >
                <option value="">-- Select Reason --</option>
                {ARCHIVE_REASONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes {reason === 'other' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setError('');
                }}
                rows={3}
                placeholder="Provide additional context (e.g., date of incident, reference documents)"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500"
              >
                Archive Certificate
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const modalRoot = document.getElementById('modal-root');
  return modalRoot ? createPortal(modalContent, modalRoot) : modalContent;
};

export default ArchiveCertificateModal;
